import React from 'react';
import { useQuery } from '@tanstack/react-query';
import PageShell from '../components/PageShell';
import { AppScopeBanner } from '../components/AppScopeBanner';
import { cpFetch } from '../lib/cpClient';
import { useActiveAppId, useSelectedAppId, useSelectedAppName, isCoreAppId } from '../hooks/useSelectedAppId';
import {
  presentLearningView,
  type DirtyLearningSummary,
} from '../features/learning/learningHonesty';
import { Brain, ShieldCheck, Activity, MessageSquare } from 'lucide-react';

function fmtDate(value: string | null | undefined) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
}

function StatCard({
  label,
  value,
  hint,
  icon: Icon,
}: {
  label: string;
  value: string | number;
  hint?: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
}) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 shadow-sm">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Icon size={16} />
        <span>{label}</span>
      </div>
      <div className="mt-2 text-2xl font-semibold">{value}</div>
      {hint ? <div className="mt-1 text-xs text-muted-foreground">{hint}</div> : null}
    </div>
  );
}

export default function LearningPage() {
  const selectedAppId = useSelectedAppId();
  const selectedAppName = useSelectedAppName();
  const activeAppId = useActiveAppId();
  const core = isCoreAppId(activeAppId, selectedAppName);

  const { data, isLoading, error } = useQuery({
    queryKey: ['learning-summary', selectedAppId, selectedAppName],
    queryFn: () => {
      const params = new URLSearchParams();
      if (selectedAppId && !core) {
        params.set('app_id', selectedAppId);
        if (selectedAppName) params.set('app_name', selectedAppName);
      }
      const qs = params.toString();
      return cpFetch<DirtyLearningSummary>(`/learning/summary${qs ? `?${qs}` : ''}`);
    },
    staleTime: 60_000,
  });

  const view = presentLearningView({
    payload: data,
    selectedAppId,
    selectedAppName,
  });
  const newestLessonMs = view.learnings.reduce((max, row) => {
    const ts = row.created_at ? new Date(row.created_at).getTime() : 0;
    return Number.isFinite(ts) && ts > max ? ts : max;
  }, 0);
  const LEARNING_STALE_MS = 14 * 24 * 60 * 60 * 1000;
  const learningsStale = newestLessonMs > 0 && Date.now() - newestLessonMs > LEARNING_STALE_MS;
  const newestLessonIso = newestLessonMs > 0 ? new Date(newestLessonMs).toISOString() : null;

  return (
    <PageShell
      title="Learning"
      subtitle="Distilled platform learnings seats may see. Customer data stays seat-owned. Agent chat is not the store."
    >
      <AppScopeBanner />
      <div className="rounded-md border border-indigo-100 bg-indigo-50 px-4 py-3 text-sm text-indigo-900">
        <div className="font-medium">{view.scope.label}</div>
        <p className="mt-1 text-xs text-indigo-800">{view.scope.note}</p>
      </div>

      {isLoading ? (
        <div className="text-sm text-muted-foreground">Loading distilled learnings…</div>
      ) : error ? (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
          Failed to load distilled learnings: {(error as Error).message}
        </div>
      ) : !view.ok ? (
        <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-4 text-sm">
          {view.empty_message || 'Learning summary unavailable on this environment.'}
        </div>
      ) : (
        <div className="space-y-6">
          <div className="grid gap-4 md:grid-cols-3">
            <StatCard
              label="Distilled learnings (this view)"
              value={view.count}
              hint={view.scope.kind === 'selected_app' ? `${view.scope.label} may see these platform-wide lessons` : 'Platform-wide store — not a selected-app customer count'}
              icon={Brain}
            />
            <StatCard
              label="Feedback (platform-wide)"
              value={view.loop_health.feedback_total}
              hint="Aggregate volume. Not selected-app customer rows. top_apps omitted."
              icon={MessageSquare}
            />
            <StatCard
              label="Promotion gate"
              value={view.gate.met ? 'Open' : 'Gated'}
              hint={`${view.gate.current_distinct_count} distinct apps / min ${view.gate.min_distinct_tenants} — min_distinct_tenants is not lowered`}
              icon={ShieldCheck}
            />
          </div>

          <section className="rounded-lg border border-border bg-card p-4">
            <h2 className="text-sm font-semibold">Loop health</h2>
            <p className="mt-1 text-xs text-muted-foreground">{view.loop_health.note}</p>
            <div className="mt-3 flex items-start gap-2 text-sm">
              <Activity size={16} className="mt-0.5 text-muted-foreground" />
              <div>
                <div className="font-medium">Feedback (24h, platform-wide)</div>
                <div className="text-muted-foreground">{view.loop_health.last_24h} events</div>
                <div className="text-xs text-muted-foreground">Latest: {fmtDate(view.loop_health.latest_timestamp)}</div>
              </div>
            </div>
          </section>

          {learningsStale && (
            <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-4 text-sm">
              Distilled learnings are stale — newest is {fmtDate(newestLessonIso)} ({view.count} lessons).
              The loop has not promoted since then. This is not a healthy/current learning story.
            </div>
          )}

          <section className="rounded-lg border border-border bg-card">
            <div className="border-b border-border px-4 py-3">
              <h2 className="text-sm font-semibold">Distilled learnings</h2>
              <p className="text-xs text-muted-foreground">
                {view.store} — read-only. Customer feedback rows are omitted.
              </p>
            </div>
            {view.empty_kind ? (
              <div className="p-6 text-sm text-muted-foreground">
                <p className="font-medium text-gray-800">
                  {view.empty_kind === 'gated' ? 'Gated — no distilled learnings yet' : 'No distilled learnings yet'}
                </p>
                <p className="mt-1">{view.empty_message}</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full table-fixed text-sm">
                  <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="w-[16%] px-4 py-2">Category</th>
                      <th className="w-[18%] px-4 py-2">Agent</th>
                      <th className="w-[12%] px-4 py-2">Tenants</th>
                      <th className="w-[16%] px-4 py-2">Created</th>
                      <th className="w-[38%] px-4 py-2">Insight</th>
                    </tr>
                  </thead>
                  <tbody>
                    {view.learnings.map((row) => (
                      <tr key={row.id || `${row.agent_id}-${row.category}`} className="border-t border-border align-top">
                        <td className="px-4 py-3 font-medium">{row.category || row.label || 'general'}</td>
                        <td className="px-4 py-3 font-mono text-xs">{row.agent_id || '—'}</td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {row.tenant_label || (row.distinct_tenant_count != null ? String(row.distinct_tenant_count) : 'no tenant evidence')}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">{fmtDate(row.created_at)}</td>
                        <td className="px-4 py-3 max-w-xl align-top">
                          <div className="max-h-40 overflow-y-auto whitespace-pre-wrap break-words text-muted-foreground">
                            {row.text}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      )}
    </PageShell>
  );
}
